
import * as React from "react"
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const ToggleGroup = ToggleGroupPrimitive.Root

// Simplified container variants with layout options
const toggleGroupVariants = cva(
  "inline-flex items-center justify-center gap-2 border border-tribal-neutral/20 rounded-md",
  {
    variants: {
      variant: {
        default: "",
        outline: "border-input bg-transparent shadow-sm",
      },
      size: {
        default: "h-9 px-3",
        sm: "h-8 px-2",
        lg: "h-10 px-4",
      },
      layout: {
        row: "flex-row", // Horizontal layout (default)
        column: "flex-col", // Vertical layout
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      layout: "row", // Default to horizontal layout
    },
  }
)

// Enhanced item variants with more styling options
const toggleGroupItemVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "data-[state=on]:bg-tribal-primary data-[state=on]:text-white data-[state=on]:shadow-sm data-[state=on]:rounded-md",
        outline: "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground",
        ghost: "hover:bg-tribal-primary/10 data-[state=on]:bg-tribal-primary/20 data-[state=on]:text-tribal-primary",
        subtle: "data-[state=on]:bg-tribal-primary/10 data-[state=on]:text-tribal-primary data-[state=on]:font-semibold",
      },
      size: {
        default: "h-9 px-3",
        sm: "h-8 px-2",
        lg: "h-10 px-4",
        icon: "h-9 w-9 p-0", // For icon-only buttons
      },
      iconPosition: {
        default: "",
        left: "flex-row",
        right: "flex-row-reverse",
        top: "flex-col",
        bottom: "flex-col-reverse",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      iconPosition: "default",
    },
  }
)

// Fixed the interface and component implementation to work with Radix UI's types
const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof toggleGroupItemVariants>
>(({ className, children, variant, size, iconPosition, ...props }, ref) => (
  <ToggleGroupPrimitive.Item
    ref={ref}
    className={cn(toggleGroupItemVariants({ variant, size, iconPosition, className }))}
    {...props}
  >
    {children}
  </ToggleGroupPrimitive.Item>
))
ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName

// Properly typed custom toggle group to avoid TypeScript errors
const ToggleGroupCustom = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof toggleGroupVariants>
>(({ className, variant, size, layout, ...props }, ref) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    className={cn(toggleGroupVariants({ variant, size, layout, className }))}
    {...props}
  />
))
ToggleGroupCustom.displayName = "ToggleGroupCustom"

export { 
  ToggleGroup, 
  ToggleGroupCustom, 
  ToggleGroupItem, 
  toggleGroupVariants, 
  toggleGroupItemVariants 
}
